/**
 * BrokeragePropertyResolver — Inc-9.
 *
 * Resolves the brokerage contract's property from the extracted registration /
 * deed number against the company's existing properties:
 *  - auto-matches the extracted number(s) → properties.deed_number OR rer_number
 *    (extraction can't tell RER from deed, so both extracted values are tried
 *    against both columns) on first render,
 *  - shows the extracted number inline + a searchable picker (pre-selected from
 *    the match, still editable — overriding to a different number warns),
 *  - when no property matches, offers a pre-filled "Create property" confirm
 *    sub-form. A property needs an owner, so creation is gated on the owner
 *    being resolved first (the resolved owner becomes the property's owner).
 *
 * Property is OPTIONAL on the contract — "No property" is always available.
 */
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProperties, useCreateProperty } from "@/hooks/useProperties";
import { useUnits } from "@/hooks/useUnits";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Building2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  extractedPropertyIdentifiers,
  matchPropertyByIdentifiers,
  normaliseId,
} from "@/lib/brokerageMatch";

export interface PropertyExtract {
  property_registration_no?: string | null;
  deed_number?: string | null;
  property_address?: string | null;
  property_type?: string | null;
  property_use?: string | null;
  property_area?: number | null;
}

interface PropertyRow {
  id: string;
  name: string;
  deed_number?: string | null;
  rer_number?: string | null;
}

interface Props {
  extracted: PropertyExtract;
  /** Selected property id, "" when none. */
  value: string;
  onChange: (propertyId: string) => void;
  /** Resolved owner id — required before a property can be created. */
  ownerId: string;
  /**
   * AC2 — unit scope. When `true` the contract covers the WHOLE property (all
   * units); when `false` the user picks specific units via `selectedUnitIds`.
   * Both are optional so callers that don't need unit granularity keep working
   * (defaults to whole-property).
   */
  allUnits?: boolean;
  onAllUnitsChange?: (allUnits: boolean) => void;
  /** Specific unit ids covered by the contract (used when `allUnits` is false). */
  selectedUnitIds?: string[];
  onUnitsChange?: (unitIds: string[]) => void;
}

const PROPERTY_TYPE_OPTIONS: { value: string; ar: string; en: string }[] = [
  { value: "residential", ar: "سكني", en: "Residential" },
  { value: "commercial", ar: "تجاري", en: "Commercial" },
  { value: "land", ar: "أرض", en: "Land" },
  { value: "villa", ar: "فيلا", en: "Villa" },
  { value: "apartment", ar: "شقة", en: "Apartment" },
  { value: "building", ar: "عمارة", en: "Building" },
  { value: "mixed", ar: "متعدد الاستخدام", en: "Mixed" },
  { value: "farm", ar: "مزرعة", en: "Farm" },
];

/** Best-effort map of the extracted Arabic type/use string to the enum. */
function guessPropertyType(...sources: (string | null | undefined)[]): string {
  const v = sources.filter(Boolean).join(" ").toLowerCase();
  if (/سكن|residential/.test(v)) return "residential";
  if (/تجار|commercial/.test(v)) return "commercial";
  if (/أرض|ارض|land/.test(v)) return "land";
  if (/فيلا|villa/.test(v)) return "villa";
  if (/شقة|شقه|apartment/.test(v)) return "apartment";
  if (/عمارة|عماره|مبنى|مبني|building/.test(v)) return "building";
  if (/مزرع|farm/.test(v)) return "farm";
  return "residential";
}

export function BrokeragePropertyResolver({
  extracted,
  value,
  onChange,
  ownerId,
  allUnits = true,
  onAllUnitsChange,
  selectedUnitIds = [],
  onUnitsChange,
}: Props) {
  const { t } = useLanguage();
  const { data: properties = [], isLoading: propsLoading } = useProperties();
  const createProperty = useCreateProperty();
  // State (not a ref) so flipping it after the auto-match attempt re-renders and
  // the "property not in system" prompt can appear.
  const [autoResolved, setAutoResolved] = useState(false);

  // rer_number isn't in the generated supabase types yet — regenerate
  // src/integrations/supabase/types.ts after this migration lands on staging
  // (N2 follow-up); this cast bridges deed_number/rer_number until then.
  const rows = properties as unknown as PropertyRow[];
  const identifiers = extractedPropertyIdentifiers(extracted);
  const extractedNumber = extracted.property_registration_no || extracted.deed_number || "";

  // Auto-match once when the user hasn't chosen yet.
  useEffect(() => {
    if (autoResolved) return;
    if (value) {
      setAutoResolved(true);
      return;
    }
    // Gate on loading (not list length) so a company with ZERO properties still
    // marks the attempt done → the "create property" prompt appears on first import.
    if (propsLoading || identifiers.length === 0) return;
    setAutoResolved(true);
    const match = matchPropertyByIdentifiers(rows, identifiers);
    if (match) onChange(match.id);
    // Deps mirror BrokerageOwnerResolver (incl. the stable `onChange` setter). The
    // eslint-disable is only because we gate on identifiers.join("|") rather than
    // the fresh `identifiers` array that's recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, value, propsLoading, identifiers.join("|"), autoResolved, onChange]);

  const selected = rows.find((p) => p.id === value) ?? null;
  const targets = new Set(identifiers);
  const mismatch =
    !!selected &&
    identifiers.length > 0 &&
    !(
      (selected.deed_number && targets.has(normaliseId(selected.deed_number))) ||
      (selected.rer_number && targets.has(normaliseId(selected.rer_number)))
    );
  const notFound = autoResolved && !value && identifiers.length > 0;

  // ── Create-property sub-form ────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: extracted.property_address || (extractedNumber ? `عقار ${extractedNumber}` : ""),
    property_type: guessPropertyType(extracted.property_type, extracted.property_use),
    deed_number: extracted.deed_number ?? "",
    rer_number: extracted.property_registration_no ?? "",
    total_area: extracted.property_area != null ? String(extracted.property_area) : "",
  });

  const handleCreate = async () => {
    if (!ownerId) {
      toast.error(t("يجب تحديد المالك أولاً", "Resolve the owner first"));
      return;
    }
    if (!form.name.trim()) {
      toast.error(t("اسم العقار مطلوب", "Property name is required"));
      return;
    }

    // AC1 — dedupe before creating. A property added earlier via a property-add
    // service request (CreateContractDialog) may already exist with a
    // differently-formatted deed/RER number. Re-run the SAME normalized matcher
    // (trim/strip separators) against the live company list and REUSE the match
    // instead of inserting a duplicate. `rows` is the company-scoped list from
    // useProperties (already filtered by company_id), so the match is in-tenant.
    const existing = matchPropertyByIdentifiers(rows, [
      normaliseId(form.deed_number),
      normaliseId(form.rer_number),
    ]);
    if (existing) {
      onChange(existing.id);
      setShowCreate(false);
      toast.info(
        t(
          "هذا العقار موجود مسبقاً — تم استخدام السجل الحالي بدلاً من إنشاء نسخة مكررة.",
          "This property already exists — reused the existing record instead of creating a duplicate.",
        ),
      );
      return;
    }

    try {
      const property = await createProperty.mutateAsync({
        name: form.name.trim(),
        property_type: form.property_type,
        deed_number: form.deed_number.trim() || undefined,
        rer_number: form.rer_number.trim() || null,
        address: extracted.property_address || undefined,
        total_area:
          form.total_area && !isNaN(Number(form.total_area)) ? Number(form.total_area) : undefined,
        owner_id: ownerId,
      });
      if (property?.id) {
        onChange(property.id);
        setShowCreate(false);
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("تعذّر إنشاء العقار", "Failed to create property"),
      );
    }
  };

  return (
    <div className="space-y-2">
      <Label>
        {t("العقار", "Property")}{" "}
        <span className="text-muted-foreground text-xs">({t("اختياري", "optional")})</span>
      </Label>

      {extractedNumber && (
        <p className="text-xs text-muted-foreground" data-testid="property-extracted-id">
          {t("من العقد — رقم العقار/الصك:", "From contract — property/deed #:")}{" "}
          <span className="font-medium text-foreground">{extractedNumber}</span>
        </p>
      )}

      <SearchableSelect
        items={rows}
        value={value || null}
        onValueChange={(v) => onChange(v ?? "")}
        getKey={(p) => p.id}
        getLabel={(p) => p.name}
        getSubtitle={(p) =>
          p.rer_number
            ? `${t("عقاري", "RER")} ${p.rer_number}`
            : p.deed_number
              ? `${t("صك", "Deed")} ${p.deed_number}`
              : null
        }
        getSearchString={(p) => `${p.name} ${p.deed_number ?? ""} ${p.rer_number ?? ""}`}
        placeholder={t("اختر عقاراً", "Select a property")}
        searchPlaceholder={t("ابحث بالاسم أو الرقم...", "Search by name or number...")}
        emptyText={t("لا يوجد عقار مطابق", "No matching property")}
        noneLabel={t("بدون عقار", "No property")}
      />

      {selected && !mismatch && identifiers.length > 0 && (
        <p className="text-xs text-success">
          {t("تم مطابقة العقار تلقائياً بالرقم", "Property auto-matched by number")}
        </p>
      )}

      {mismatch && (
        <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-500">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {t(
            `رقم العقار المختار لا يطابق الرقم المستخرج (${extractedNumber})`,
            `Selected property's number doesn't match the extracted number (${extractedNumber})`,
          )}
        </p>
      )}

      {!showCreate && notFound && !selected && (
        <div className="rounded-lg border border-dashed p-3 text-sm space-y-2">
          <p className="text-muted-foreground">
            {t("هذا العقار غير موجود في النظام.", "This property isn't in the system.")}
          </p>
          {ownerId ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 me-2" />
              {t("إنشاء العقار من العقد", "Create property from contract")}
            </Button>
          ) : (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              {t("حدّد المالك أولاً لإنشاء العقار", "Resolve the owner first to create the property")}
            </p>
          )}
        </div>
      )}

      {showCreate && (
        <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
          <p className="text-sm font-medium">{t("إنشاء عقار جديد", "Create new property")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">{t("اسم العقار", "Property name")} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-field="new_property_name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("نوع العقار", "Type")}</Label>
              <Select
                value={form.property_type}
                onValueChange={(v) => setForm((f) => ({ ...f, property_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {t(o.ar, o.en)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("المساحة (م²)", "Area (m²)")}</Label>
              <Input
                value={form.total_area}
                type="number"
                onChange={(e) => setForm((f) => ({ ...f, total_area: e.target.value }))}
                data-field="new_property_area"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("رقم الصك", "Deed #")}</Label>
              <Input
                value={form.deed_number}
                onChange={(e) => setForm((f) => ({ ...f, deed_number: e.target.value }))}
                data-field="new_property_deed"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("الرقم العقاري", "RER #")}</Label>
              <Input
                value={form.rer_number}
                onChange={(e) => setForm((f) => ({ ...f, rer_number: e.target.value }))}
                data-field="new_property_rer"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowCreate(false)}>
              {t("إلغاء", "Cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleCreate}
              disabled={createProperty.isPending || !ownerId}
            >
              {createProperty.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t("إنشاء العقار", "Create property")}
            </Button>
          </div>
        </div>
      )}

      {/* AC2 — unit scope: whole property (all units) or specific unit(s). Shown
          only once a property is selected. The picker is its own component so
          useUnits(propertyId) mounts/unmounts with the selection (hook order
          stays stable). */}
      {value && (
        <BrokerageUnitScopePicker
          propertyId={value}
          allUnits={allUnits}
          onAllUnitsChange={onAllUnitsChange}
          selectedUnitIds={selectedUnitIds}
          onUnitsChange={onUnitsChange}
        />
      )}
    </div>
  );
}

/**
 * AC2 — choose whether the brokerage contract covers the WHOLE property (all
 * units) or SPECIFIC units. Mirrors the rental wizard's unit-selection pattern
 * (useUnits(propertyId) + per-unit rows) for consistency, kept self-contained
 * so the parent's hook order never depends on whether a property is selected.
 */
function BrokerageUnitScopePicker({
  propertyId,
  allUnits,
  onAllUnitsChange,
  selectedUnitIds,
  onUnitsChange,
}: {
  propertyId: string;
  allUnits: boolean;
  onAllUnitsChange?: (allUnits: boolean) => void;
  selectedUnitIds: string[];
  onUnitsChange?: (unitIds: string[]) => void;
}) {
  const { t } = useLanguage();
  const { data: units = [], isLoading } = useUnits(propertyId);

  const toggleUnit = (unitId: string, checked: boolean) => {
    if (!onUnitsChange) return;
    const next = new Set(selectedUnitIds);
    if (checked) next.add(unitId);
    else next.delete(unitId);
    onUnitsChange([...next]);
  };

  return (
    <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
      <Label className="flex items-center gap-2 text-sm">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        {t("نطاق العقد", "Contract scope")}
      </Label>

      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <Checkbox
          checked={allUnits}
          onCheckedChange={(v) => onAllUnitsChange?.(Boolean(v))}
          aria-label={t("كل الوحدات / العقار بالكامل", "All units / whole property")}
        />
        <span>
          {t("كل الوحدات (العقار بالكامل)", "All units (whole property)")}
        </span>
      </label>

      {!allUnits && (
        <div className="space-y-2 ps-1">
          <p className="text-xs text-muted-foreground">
            {t("اختر الوحدات المشمولة بالعقد:", "Select the units covered by the contract:")}
          </p>
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("جارٍ تحميل الوحدات…", "Loading units…")}
            </div>
          ) : units.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("لا توجد وحدات لهذا العقار", "This property has no units")}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
              {units.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 text-sm cursor-pointer rounded-md border p-2"
                >
                  <Checkbox
                    aria-label={`${t("وحدة", "Unit")} ${u.unit_number}`}
                    checked={selectedUnitIds.includes(u.id)}
                    onCheckedChange={(v) => toggleUnit(u.id, Boolean(v))}
                  />
                  <span className="truncate">
                    {t("وحدة", "Unit")} {u.unit_number}
                  </span>
                </label>
              ))}
            </div>
          )}
          {selectedUnitIds.length === 0 && !isLoading && units.length > 0 && (
            <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              {t(
                "اختر وحدة واحدة على الأقل أو فعّل \"كل الوحدات\".",
                'Select at least one unit, or enable "All units".',
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
